import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pencil, Play, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { FREQUENCY_LABEL, indentedName, MONTHS, useAssignees, useDepartments, WEEKDAYS } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatRelative } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import type { RecurrenceFrequency, RecurrencePreview, RecurringTicket, WorkOrderCategory, WorkOrderPriority } from '../lib/types';
import { WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL } from '../lib/work-orders';
import { Placeholders } from './ActionsEditor';
import { AssetSearch, RoomPicker } from './pickers';
import { useToast } from './toast';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageLoader,
  Segmented,
  Select,
  Switch,
  Table,
  Td,
  Textarea,
  Th,
} from './ui';

interface FormState {
  name: string;
  enabled: boolean;
  title: string;
  body: string;
  category: WorkOrderCategory;
  priority: WorkOrderPriority;
  departmentId: string;
  assignedToId: string;
  asset: { id: string; assetTag: string; name: string } | null;
  room: { id: string; name: string } | null;
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays: number[];
  dayOfMonth: number;
  month: number;
  timeOfDay: string;
  startDate: string;
  endDate: string;
  skipClosedDays: boolean;
}

const UNIT: Record<RecurrenceFrequency, string> = { DAILY: 'days', WEEKLY: 'weeks', MONTHLY: 'months', YEARLY: 'years' };

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function toForm(template: RecurringTicket | null): FormState {
  if (!template) {
    const start = today();
    return {
      name: '',
      enabled: true,
      title: '',
      body: '',
      category: 'OTHER',
      priority: 'NORMAL',
      departmentId: '',
      assignedToId: '',
      asset: null,
      room: null,
      frequency: 'YEARLY',
      interval: 1,
      weekdays: [1],
      dayOfMonth: 15,
      month: 6,
      timeOfDay: '07:00',
      startDate: start,
      endDate: '',
      skipClosedDays: true,
    };
  }
  const start = new Date(`${template.startDate}T12:00:00Z`);
  return {
    name: template.name,
    enabled: template.enabled,
    title: template.title,
    body: template.body,
    category: template.category,
    priority: template.priority,
    departmentId: template.departmentId ?? '',
    assignedToId: template.assignedToId ?? '',
    asset: template.asset,
    room: template.room,
    frequency: template.frequency,
    interval: template.interval,
    weekdays: template.weekdays.length ? template.weekdays : [start.getUTCDay()],
    dayOfMonth: template.dayOfMonth ?? start.getUTCDate(),
    month: template.month ?? start.getUTCMonth() + 1,
    timeOfDay: template.timeOfDay,
    startDate: template.startDate,
    endDate: template.endDate ?? '',
    skipClosedDays: template.skipClosedDays,
  };
}

function scheduleBody(form: FormState) {
  return {
    frequency: form.frequency,
    interval: form.interval,
    weekdays: form.frequency === 'WEEKLY' ? form.weekdays : [],
    dayOfMonth: form.frequency === 'MONTHLY' || form.frequency === 'YEARLY' ? form.dayOfMonth : null,
    month: form.frequency === 'YEARLY' ? form.month : null,
    timeOfDay: form.timeOfDay,
    startDate: form.startDate,
    endDate: form.endDate || null,
    skipClosedDays: form.skipClosedDays,
  };
}

function RecurringTicketModal({ template, open, onClose }: { template: RecurringTicket | null; open: boolean; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const departments = useDepartments();
  const assignees = useAssignees();
  const [form, setForm] = useState<FormState>(() => toForm(template));
  const [error, setError] = useState('');
  const update = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (open) {
      setForm(toForm(template));
      setError('');
    }
  }, [open, template]);

  const scheduleKey = useDebouncedValue(JSON.stringify(scheduleBody(form)), 300);
  const schedule = useMemo(() => JSON.parse(scheduleKey) as ReturnType<typeof scheduleBody>, [scheduleKey]);
  const scheduleValid = !!schedule.startDate && /^\d{2}:\d{2}$/.test(schedule.timeOfDay) && (schedule.frequency !== 'WEEKLY' || schedule.weekdays.length > 0);
  const preview = useQuery({
    queryKey: ['recurring-tickets', 'preview', scheduleKey],
    queryFn: () => api.post<RecurrencePreview>('/recurring-tickets/preview', schedule),
    enabled: open && scheduleValid,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        enabled: form.enabled,
        title: form.title,
        body: form.body,
        category: form.category,
        priority: form.priority,
        departmentId: form.departmentId || null,
        assignedToId: form.assignedToId || null,
        assetId: form.asset?.id ?? null,
        roomId: form.room?.id ?? null,
        ...scheduleBody(form),
      };
      return template ? api.patch(`/recurring-tickets/${template.id}`, body) : api.post('/recurring-tickets', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recurring-tickets'] });
      toast.success('Schedule saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  const upcoming = (preview.data?.upcoming ?? []).map((date) =>
    new Intl.DateTimeFormat('en-US', { dateStyle: 'full', timeStyle: 'short', timeZone: preview.data!.timezone }).format(new Date(date)),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={template ? `Edit ${template.name}` : 'New scheduled ticket'}
      description="Opens the same ticket on a repeating schedule, so recurring work never gets forgotten."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="recurring-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="recurring-form" onSubmit={submit} className="space-y-6">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Name" required>
            <Input value={form.name} maxLength={120} required onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Summer device maintenance" />
          </Field>
          <div className="sm:mt-6">
            <Switch checked={form.enabled} onChange={(enabled) => update({ enabled })} label="Turned on" />
          </div>
        </div>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Schedule</h3>
          <Segmented
            value={form.frequency}
            onChange={(frequency) => update({ frequency })}
            options={(Object.keys(FREQUENCY_LABEL) as RecurrenceFrequency[]).map((value) => ({ value, label: FREQUENCY_LABEL[value] }))}
          />
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
            <span>Every</span>
            <Input type="number" min={1} max={365} value={String(form.interval)} onChange={(e) => update({ interval: Number(e.target.value) || 1 })} className="w-20" aria-label="Interval" />
            <span>{UNIT[form.frequency]}</span>
            {form.frequency === 'YEARLY' && (
              <>
                <span>on</span>
                <Select value={String(form.month)} onChange={(e) => update({ month: Number(e.target.value) })} className="w-auto" aria-label="Month">
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </Select>
              </>
            )}
            {(form.frequency === 'MONTHLY' || form.frequency === 'YEARLY') && (
              <>
                <span>{form.frequency === 'MONTHLY' ? 'on day' : ''}</span>
                <Input type="number" min={1} max={31} value={String(form.dayOfMonth)} onChange={(e) => update({ dayOfMonth: Number(e.target.value) || 1 })} className="w-20" aria-label="Day of the month" />
              </>
            )}
            <span>at</span>
            <Input type="time" value={form.timeOfDay} onChange={(e) => update({ timeOfDay: e.target.value })} className="w-32" aria-label="Time" />
          </div>
          {form.frequency === 'WEEKLY' && (
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((day, index) => {
                const active = form.weekdays.includes(index);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => update({ weekdays: active ? form.weekdays.filter((d) => d !== index) : [...form.weekdays, index].sort() })}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
                      active ? 'bg-brand-700 text-white ring-brand-700' : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50',
                    )}
                  >
                    {day.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Starting">
              <Input type="date" value={form.startDate} required onChange={(e) => update({ startDate: e.target.value })} />
            </Field>
            <Field label="Ending" hint="Optional">
              <Input type="date" value={form.endDate} onChange={(e) => update({ endDate: e.target.value })} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.skipClosedDays} onChange={(e) => update({ skipClosedDays: e.target.checked })} className="size-4 rounded border-slate-300" />
            If it lands on a weekend or holiday, open it on the next school day
          </label>
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            {preview.error ? (
              <p className="text-red-700">{errorMessage(preview.error)}</p>
            ) : preview.data ? (
              <>
                <p className="font-medium text-slate-800">{preview.data.description}</p>
                {upcoming.length ? (
                  <ul className="mt-1 space-y-0.5 text-slate-600">
                    {upcoming.map((date) => (
                      <li key={date}>{date}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-slate-500">No more dates before the end date.</p>
                )}
              </>
            ) : (
              <p className="text-slate-500">Upcoming dates appear here.</p>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">The ticket</h3>
          <Field label="Title" required>
            <Input value={form.title} maxLength={150} required onChange={(e) => update({ title: e.target.value })} placeholder="e.g. Summer device maintenance {{year}}" />
          </Field>
          <Field label="Details" required>
            <Textarea rows={3} maxLength={5000} required value={form.body} onChange={(e) => update({ body: e.target.value })} placeholder="A checklist of what needs doing" />
          </Field>
          <Placeholders names={['date', 'weekday', 'month', 'year']} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Type of problem">
              <Select value={form.category} onChange={(e) => update({ category: e.target.value as WorkOrderCategory })}>
                {(Object.keys(WORK_ORDER_CATEGORY_LABEL) as WorkOrderCategory[]).map((category) => (
                  <option key={category} value={category}>
                    {WORK_ORDER_CATEGORY_LABEL[category]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => update({ priority: e.target.value as WorkOrderPriority })}>
                {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
                  <option key={priority} value={priority}>
                    {WORK_ORDER_PRIORITY_LABEL[priority]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Department">
              <Select value={form.departmentId} onChange={(e) => update({ departmentId: e.target.value })}>
                <option value="">No department</option>
                {departments.data?.map((department) => (
                  <option key={department.id} value={department.id}>
                    {indentedName(department)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Assign to">
              <Select value={form.assignedToId} onChange={(e) => update({ assignedToId: e.target.value })}>
                <option value="">Nobody (let workflows assign it)</option>
                {assignees.data?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">Device (optional)</span>
              {form.asset ? (
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-mono text-xs font-semibold text-brand-800">{form.asset.assetTag}</span>
                  <span className="min-w-0 flex-1 truncate">{form.asset.name}</span>
                  <button type="button" onClick={() => update({ asset: null })} className="rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label="Clear device">
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <AssetSearch inline onSelect={(asset) => update({ asset: { id: asset.id, assetTag: asset.assetTag, name: asset.name } })} />
              )}
            </div>
            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">Room (optional)</span>
              {form.room ? (
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">{form.room.name}</span>
                  <button type="button" onClick={() => update({ room: null })} className="rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label="Clear room">
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <RoomPicker inline value={null} onChange={(room) => room && update({ room: { id: room.id, name: room.name } })} />
              )}
            </div>
          </div>
        </section>
      </form>
    </Modal>
  );
}

export function RecurringTicketsPanel() {
  const { can } = useAuth();
  const canEdit = can('ADMIN');
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RecurringTicket | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RecurringTicket | null>(null);
  const templates = useQuery({ queryKey: ['recurring-tickets'], queryFn: () => api.get<RecurringTicket[]>('/recurring-tickets') });
  const refresh = () => {
    for (const key of ['recurring-tickets', 'automation-runs', 'work-orders']) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.patch(`/recurring-tickets/${id}`, { enabled }),
    onSuccess: () => refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const openNow = useMutation({
    mutationFn: (id: string) => api.post<{ id: string; number: number }>(`/recurring-tickets/${id}/run`),
    onSuccess: (ticket) => {
      refresh();
      toast.success(`Opened #${ticket.number}`);
      navigate(`/work-orders/${ticket.id}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/recurring-tickets/${id}`),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      toast.success('Schedule deleted');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Scheduled tickets"
        description="Open recurring work daily, weekly, monthly, or yearly: summer maintenance, device refresh, backup checks."
        actions={
          canEdit && (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              New schedule
            </Button>
          )
        }
      />
      {templates.isLoading ? (
        <PageLoader />
      ) : templates.error ? (
        <ErrorState error={templates.error} onRetry={() => templates.refetch()} />
      ) : templates.data!.length === 0 ? (
        <EmptyState icon={<CalendarClock />} title="No scheduled tickets yet" description="For example: open “Summer device maintenance” every June 15." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Schedule</Th>
              <Th>Repeats</Th>
              <Th>Next ticket</Th>
              <Th>Last opened</Th>
              <Th className="text-right">On</Th>
              {canEdit && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {templates.data!.map((template) => (
              <tr key={template.id}>
                <Td className="max-w-xs">
                  <p className="font-medium text-slate-900">{template.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {[template.department?.name, template.assignedTo?.name, template.asset?.assetTag].filter(Boolean).join(' · ') || template.title}
                  </p>
                </Td>
                <Td className="text-slate-700">{template.schedule}</Td>
                <Td className="whitespace-nowrap text-slate-600">{template.enabled && template.nextRunAt ? formatDateTime(template.nextRunAt) : '—'}</Td>
                <Td className="whitespace-nowrap text-slate-500">{template.lastRunAt ? formatRelative(template.lastRunAt) : 'Never'}</Td>
                <Td className="text-right">
                  <div className="inline-flex">
                    <Switch
                      checked={template.enabled}
                      disabled={!canEdit}
                      onChange={(enabled) => toggle.mutate({ id: template.id, enabled })}
                      label={<span className="sr-only">Turn {template.enabled ? 'off' : 'on'}</span>}
                    />
                  </div>
                </Td>
                {canEdit && (
                  <Td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" icon={<Play />} loading={openNow.isPending && openNow.variables === template.id} onClick={() => openNow.mutate(template.id)}>
                      Open now
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(template)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(template)} aria-label={`Delete ${template.name}`} />
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <RecurringTicketModal open={!!editing} template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={`Delete “${deleting?.name}”?`}
        description="Tickets it already opened stay. No more will be opened."
        confirmLabel="Delete schedule"
      />
    </Card>
  );
}
